import { Button } from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { UserIcon } from '@vrplatform/icons';
import { AvatarGroup, Drawer, Logo } from '@vrplatform/components-legacy';
import { useAppName } from '@vrplatform/theme';
import { parseAsBoolean, useQueryState } from '@vrplatform/query-params';
import { Center, LoadingOverlay, Text } from '@mantine/core';
import { formatAddress } from '@vrplatform/ui-common';
import { useState } from 'react';
import { DrawerHeader } from '../_components';
import { OwnerEditForm, type OwnerEditFormInputs } from './OwnerEditForm';
import { useOwnerDetailDrawer } from '..';
import { removeDrawerStateFromUrl } from '../../utils';

export function useAddOwnerDrawer() {
  const [opened, setOpened] = useQueryState('add-owner', parseAsBoolean);

  return {
    opened: !!opened,
    open: () => {
      removeDrawerStateFromUrl();
      setOpened(true);
    },
    close: () => setOpened(false),
  };
}

type Props = {
  onSuccessOpenOwner?: boolean;
  onSuccess?: (contactId: string) => void;
};

export const AddOwnerDrawer = ({
  onSuccessOpenOwner = true,
  onSuccess,
}: Props) => {
  const { themeName } = useAppName();
  const { opened, close } = useAddOwnerDrawer();
  const { open: openOwnerDetailDrawer } = useOwnerDetailDrawer();

  const [view, setView] = useState<
    { key: 'form'; data: null } | { key: 'pm-exists'; data: null }
  >({ data: null, key: 'form' });

  const { mutateAsync, isPending: loading } = useApiMutation(
    'post',
    '/contacts'
  );

  const invalidate = useInvalidateApiQueries(['owners']);

  const submit = async (formData: OwnerEditFormInputs) =>
    await mutateAsync({
      body: {
        payoutAccountId: formData.defaultAccountId || null,
        type: 'owner',
        email: formData.email?.trim() || undefined,
        phone: formData.phone?.trim() || undefined,
        companyType: formData.type === 'individual' ? null : formData.type,
        firstName:
          formData.type === 'individual'
            ? formData.firstName?.trim() || undefined
            : null,
        name: formData.lastName?.trim() || undefined,
        taxIdentifier: formData.taxId?.trim() || undefined,
        address:
          formData.address.countryCode &&
          formData.address.stateCode &&
          formData.address.line1?.trim()
            ? {
                city: formData.address.city.trim(),
                line1: formData.address.line1.trim(),
                line2: formData.address.line2?.trim() || undefined,
                stateCode: formData.address.stateCode,
                countryCode: formData.address.countryCode,
                postalCode: formData.address.postcode.trim(),
                full: formatAddress(formData.address),
              }
            : undefined,
        invite: formData.addUserOption,
      },
    })
      .then((result) => {
        if (onSuccessOpenOwner) openOwnerDetailDrawer(result.data.id);
        if (onSuccess) onSuccess(result.data.id);
        invalidate();
        close();
      })
      .catch((error) => {
        const message = error?.message.includes(
          'already member as a property manager'
        );

        if (message) {
          setView({ data: null, key: 'pm-exists' });
        } else {
          showApiErrorNotification({
            error,
            title: "We couldn't add the owner",
          });
        }
      });

  return (
    <Drawer
      opened={opened}
      onClose={close}
      containerSx={{
        position: 'relative',
      }}
    >
      {view.key === 'form' && (
        <>
          <DrawerHeader
            title={'Add owner'}
            closeDrawer={close}
            containerSx={{ marginBottom: 0 }}
          />
          <OwnerEditForm
            onReset={close}
            submitButtonLabel={'Add owner'}
            handleSubmit={submit}
            isOwnerAddModal
            initialValues={undefined}
          />
        </>
      )}
      {view.key === 'pm-exists' && (
        <Center mih="100%" py="xl" px="md" sx={{ flexDirection: 'column' }}>
          <AvatarGroup
            leftIcon={<Logo width={80} />}
            leftBgColor={themeName === 'vrplatform' ? 'white' : undefined}
            rightIcon={<UserIcon size={48} strokeWidth={1} />}
            rightBgColor="white"
          />
          <Text component="h4" size="xl" my="lg" ta="center" fw={500}>
            PM user already exists
          </Text>
          <Text component="p" mt={0} mb={32} ta="center" c="neutral">
            We couldn't add this email address as person. The user already
            exists as a PM team member.
          </Text>

          <Button
            onClick={() => {
              invalidate();
              close();
            }}
          >
            Cancel
          </Button>
        </Center>
      )}
      <LoadingOverlay visible={loading} />
    </Drawer>
  );
};
